import { Suspense, lazy, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";

import { fetchAdbExplorer, type AdbCorrelationScore, type AdbExplorerColumn, type AdbExplorerLayer, type AdbExplorerPayload } from "../../api/adbExplorer";
import type { CorrelogramColumn } from "../../components/data-explorer/Correlogram";
import { rankColumnsByCorrelationTo } from "../../components/data-explorer/correlationRanking";
import type { ThreadRow } from "../thread-design-system";
import type { V3WorkbenchData } from "../thread-v3/types";
import { Icons } from "../thread-v4/icons";
import { formatValue, rowKey } from "../thread-v4/ThreadV4Widgets";
import type { SurfaceProps } from "./ThreadV5Surfaces";
import {
  DEFAULT_TAILOR_TRUST_LIMITS,
  correctionForPair,
  fitTailorSliceModel,
  recommendTailorRuns,
  summarizeTailorEvidence,
  tailorValueForMode,
  type TailorEvidenceMode,
  type TailorTrustLimits,
} from "./adbTailorSlice";

const DataExplorerCorrelogram = lazy(() =>
  import("../../components/data-explorer/Correlogram").then((module) => ({ default: module.Correlogram })),
);

const OUTPUT_PRIORITY = ["CL", "C_L", "cl", "CD", "C_D", "cd", "Cm", "C_m", "Cm_pitch", "CY", "Cn_yaw", "Cl_roll", "CL_alpha", "Cm_alpha"];
const OUTPUT_TOKENS = new Set(["cl", "cd", "cm", "cy", "cn", "cf", "lift", "drag", "moment", "force", "coefficient", "pressure", "friction", "omega", "stability", "metric", "output", "result"]);
const AXIS_TOKENS = new Set(["alpha", "aoa", "beta", "sideslip", "mach", "altitude", "re", "reynolds", "elevator", "aileron", "rudder", "flap", "rpm", "throttle", "thrust", "velocity", "speed", "theta", "phi", "gap", "deflection"]);
const MODEL_LAYER_COLORS = ["#327a5f", "#1e6f8f", "#a7662b", "#7358a5", "#9c5d68", "#60733f"];
const TAILOR_EVIDENCE_MODES: Array<{ id: TailorEvidenceMode; label: string; title: string }> = [
  { id: "raw", label: "Raw", title: "Sampled ND conditions projected into each visible 2D slice." },
  { id: "compatible", label: "Compatible", title: "Only sampled points close to the held-axis constants; direct slice evidence." },
  { id: "corrected", label: "Corrected", title: "Sampled values transported to each held slice with model(raw) -> model(slice) correction." },
];

function token(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function words(value: string): string[] {
  return value.toLowerCase().split(/[^a-z0-9]+/g).filter(Boolean);
}

function numericValue(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function labelFor(field: string): string {
  const known: Record<string, string> = {
    CL: "C_L",
    cl: "C_L",
    CD: "C_D",
    cd: "C_D",
    Cm: "C_m",
    cm: "C_m",
    Cm_pitch: "C_m pitch",
    Cl_roll: "C_l roll",
    Cn_yaw: "C_n yaw",
    CY: "C_Y",
    alpha: "alpha",
    beta: "beta",
    mach: "Mach",
  };
  return known[field] ?? field.replace(/^_+/, "").replace(/_/g, " ");
}

function isMetadata(field: string): boolean {
  const normal = token(field);
  return field.startsWith("_") || normal.endsWith("id") || normal === "index" || normal === "statuscode" || normal.includes("timestamp");
}

function isOutput(field: string): boolean {
  const normal = token(field);
  if (OUTPUT_PRIORITY.map(token).includes(normal)) return true;
  if (/^(cl|cd|cm|cy|cn|cf)/.test(normal)) return true;
  return words(field).some((word) => OUTPUT_TOKENS.has(word));
}

function isAxis(field: string): boolean {
  return !isOutput(field) && words(field).some((word) => AXIS_TOKENS.has(word));
}

function columnStats(rows: Record<string, unknown>[], field: string): Pick<AdbExplorerColumn, "sample_count" | "min" | "max" | "mean" | "distinct_count"> {
  const values = rows.map((row) => numericValue(row[field])).filter((value): value is number => value !== null);
  if (!values.length) return { sample_count: 0, min: null, max: null, mean: null, distinct_count: 0 };
  return {
    sample_count: values.length,
    min: Math.min(...values),
    max: Math.max(...values),
    mean: values.reduce((sum, value) => sum + value, 0) / values.length,
    distinct_count: new Set(values.map((value) => value.toPrecision(12))).size,
  };
}

function pearson(rows: Record<string, unknown>[], xField: string, yField: string): number | null {
  const pairs = rows
    .map((row) => [numericValue(row[xField]), numericValue(row[yField])] as const)
    .filter((pair): pair is readonly [number, number] => pair[0] !== null && pair[1] !== null);
  if (pairs.length < 3) return null;
  const xs = pairs.map((pair) => pair[0]);
  const ys = pairs.map((pair) => pair[1]);
  const mx = xs.reduce((sum, value) => sum + value, 0) / xs.length;
  const my = ys.reduce((sum, value) => sum + value, 0) / ys.length;
  let num = 0;
  let dx = 0;
  let dy = 0;
  for (let i = 0; i < xs.length; i += 1) {
    const xv = xs[i]! - mx;
    const yv = ys[i]! - my;
    num += xv * yv;
    dx += xv * xv;
    dy += yv * yv;
  }
  return dx > 0 && dy > 0 ? num / Math.sqrt(dx * dy) : null;
}

function inferLayer(row: Record<string, unknown>): AdbExplorerLayer {
  const raw = ["model_layer", "fidelity", "source_tool", "source", "solver", "method", "family", "geometry_id"]
    .map((field) => row[field])
    .find((value) => value != null && String(value).trim());
  const text = String(raw ?? "observed");
  const normal = token(text);
  if (/corrected|transport|surrogate|residual/.test(normal)) return { id: "corrected", label: "Corrected / model-conditioned", evidence: "model-conditioned", count: 0 };
  if (/flow360|cfd|rans|highfi|highfidelity/.test(normal)) return { id: "high_fidelity", label: "High-fidelity", evidence: "observed", count: 0 };
  if (/avl|bet|vlm|lowfi|lowfidelity/.test(normal)) return { id: "low_fidelity", label: "Low-fidelity", evidence: "observed", count: 0 };
  return { id: "observed", label: text === "observed" ? "Observed" : text, evidence: "observed", count: 0 };
}

function fallbackExplorer(data: V3WorkbenchData, projectId: string, branch: string): AdbExplorerPayload {
  const rows = data.rows as unknown as Record<string, unknown>[];
  const numericFields = Array.from(new Set(rows.slice(0, 500).flatMap((row) =>
    Object.entries(row)
      .filter(([field, value]) => numericValue(value) !== null && !isMetadata(field))
      .map(([field]) => field),
  )));
  const outputs = numericFields.filter(isOutput);
  const axes = numericFields.filter((field) => !outputs.includes(field) && isAxis(field));
  const flexibleOutputs = outputs.length ? outputs : numericFields.filter((field) => !axes.includes(field));
  const defaultOutput = OUTPUT_PRIORITY.find((field) => flexibleOutputs.includes(field)) ?? flexibleOutputs[0] ?? null;
  const layers = new Map<string, AdbExplorerLayer>();
  const projectedRows = rows.slice(0, 5000).map((row, index) => {
    const layer = inferLayer(row);
    layers.set(layer.id, { ...layer, count: (layers.get(layer.id)?.count ?? 0) + 1 });
    return {
      ...row,
      _row_id: String(row.runId ?? row.run_id ?? row.name ?? rowKey(row as ThreadRow) ?? `row-${index}`),
      _model_layer: layer.id,
      _evidence: layer.evidence,
    };
  });
  const correlations = Object.fromEntries(flexibleOutputs.map((output) => [
    output,
    numericFields
      .filter((field) => field !== output)
      .map((field) => {
        const r = pearson(rows, field, output);
        return r == null ? null : { field, label: labelFor(field), r, evidence: isAxis(field) ? "observed" : "model-conditioned" } satisfies AdbCorrelationScore;
      })
      .filter((item): item is AdbCorrelationScore => item !== null)
      .sort((left, right) => Math.abs(right.r) - Math.abs(left.r))
      .slice(0, 8),
  ]));
  return {
    project_id: projectId,
    branch,
    row_count: rows.length,
    sample_count: projectedRows.length,
    outputs: flexibleOutputs.map((field) => ({ id: field, label: labelFor(field), role: "output", evidence: /model|pred|surrogate|corrected|teacher/i.test(field) ? "model-conditioned" : "observed", priority: OUTPUT_PRIORITY.indexOf(field), ...columnStats(rows, field) })),
    axes: axes.map((field) => ({ id: field, label: labelFor(field), role: "axis", held_default: columnStats(rows, field).mean, ...columnStats(rows, field) })),
    default_output: defaultOutput,
    default_axes: { x: axes[0] ?? null, y: axes[1] ?? null },
    model_layers: Array.from(layers.values()),
    rows: projectedRows,
    correlations,
    findings: [],
    evidence: {
      raw_count: projectedRows.length,
      direct_count: projectedRows.filter((row) => row._evidence === "observed").length,
      compatible_count: projectedRows.filter((row) => row._evidence === "observed").length,
      corrected_count: projectedRows.filter((row) => row._model_layer === "corrected").length,
    },
    warnings: flexibleOutputs.length ? [] : ["No numeric outputs were discovered in the current table."],
  };
}

function bestDefaultOutput(explorer: AdbExplorerPayload | undefined): string {
  if (!explorer?.outputs.length) return "";
  return explorer.default_output ?? OUTPUT_PRIORITY.find((field) => explorer.outputs.some((output) => output.id === field)) ?? explorer.outputs[0]!.id;
}

function selectedOutputLabel(explorer: AdbExplorerPayload, selectedOutput: string): string {
  return explorer.outputs.find((output) => output.id === selectedOutput)?.label ?? labelFor(selectedOutput);
}

function compactNumber(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "-";
  if (Math.abs(value) >= 1000) return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (Math.abs(value) >= 10) return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return value.toLocaleString(undefined, { maximumSignificantDigits: 4 });
}

function axisDefaultValue(axis: AdbExplorerColumn): number {
  return numericValue(axis.held_default) ?? numericValue(axis.mean) ?? numericValue(axis.min) ?? 0;
}

function initialHeldValues(axes: AdbExplorerColumn[]): Record<string, number> {
  return Object.fromEntries(axes.map((axis) => [axis.id, axisDefaultValue(axis)]));
}

function axisOptionLabel(axis: AdbExplorerColumn | undefined): string {
  return axis ? axis.label : "axis";
}

function trustLimitsForLevel(level: "strict" | "balanced" | "wide"): TailorTrustLimits {
  if (level === "strict") {
    return {
      ...DEFAULT_TAILOR_TRUST_LIMITS,
      hiddenRmsLimit: 0.12,
      hiddenMaxDeltaLimit: 0.24,
      correctionAbsLimit: 0.12,
      correctionRelativeLimit: 0.25,
      compatibleRmsLimit: 0.055,
      compatibleMaxDeltaLimit: 0.14,
    };
  }
  if (level === "wide") {
    return {
      ...DEFAULT_TAILOR_TRUST_LIMITS,
      hiddenRmsLimit: 0.26,
      hiddenMaxDeltaLimit: 0.52,
      correctionAbsLimit: 0.28,
      correctionRelativeLimit: 0.55,
      compatibleRmsLimit: 0.105,
      compatibleMaxDeltaLimit: 0.24,
    };
  }
  return DEFAULT_TAILOR_TRUST_LIMITS;
}

function evidenceModeDescription(mode: TailorEvidenceMode): string {
  if (mode === "raw") return "raw projected samples";
  if (mode === "compatible") return "direct compatible slice evidence";
  return "model-conditioned corrected slice evidence";
}

function reviewSentence(explorer: AdbExplorerPayload, selectedOutput: string): string {
  const existing = explorer.findings.find((finding) => finding.includes(selectedOutputLabel(explorer, selectedOutput))) ?? explorer.findings[0];
  if (existing) return existing;
  const drivers = (explorer.correlations[selectedOutput] ?? []).filter((item) => explorer.axes.some((axis) => axis.id === item.field));
  if (!drivers.length) return `${selectedOutputLabel(explorer, selectedOutput)} has insufficient direct evidence for a driver review.`;
  const strong = drivers.filter((item) => Math.abs(item.r) >= 0.45).slice(0, 2).map((item) => item.label);
  const weak = drivers.find((item) => Math.abs(item.r) < 0.45);
  const primary = strong.length ? `primarily ${strong.join("/")}` : "no dominant direct driver";
  return `${selectedOutputLabel(explorer, selectedOutput)} is ${primary}${weak ? `, weak ${weak.label} coupling` : ""}, ${explorer.evidence.direct_count < 8 ? "insufficient direct evidence at this held slice" : `${explorer.evidence.direct_count} direct samples in view`}.`;
}

function layerColor(index: number): string {
  return MODEL_LAYER_COLORS[index % MODEL_LAYER_COLORS.length]!;
}

function ExplorerStat({ label, value, tone }: { label: string; value: ReactNode; tone?: "warn" | "ok" }) {
  return <span className={`v5-adb-stat ${tone ? `is-${tone}` : ""}`}><small>{label}</small><strong>{value}</strong></span>;
}

export function AdbExplorerSurface(props: SurfaceProps) {
  const { data, dataLoading, projectId, projectName, branch, persistedProject, selectedRunId, onSelectRun, onOpenView } = props;
  const query = useQuery({
    queryKey: ["v5-adb-explorer", projectId, branch],
    queryFn: () => fetchAdbExplorer(projectId, branch, 5000),
    enabled: persistedProject,
    staleTime: 60_000,
  });
  const fallback = useMemo(() => fallbackExplorer(data, projectId, branch), [branch, data, projectId]);
  const explorer = query.data ?? fallback;
  const [selectedOutput, setSelectedOutput] = useState(() => bestDefaultOutput(explorer));
  const [layerFilter, setLayerFilter] = useState("all");
  const [evidenceMode, setEvidenceMode] = useState<TailorEvidenceMode>("corrected");
  const [focusXAxis, setFocusXAxis] = useState(() => explorer.default_axes.x ?? explorer.axes[0]?.id ?? "");
  const [focusYAxis, setFocusYAxis] = useState(() => explorer.default_axes.y ?? explorer.axes.find((axis) => axis.id !== (explorer.default_axes.x ?? explorer.axes[0]?.id))?.id ?? "");
  const [heldValues, setHeldValues] = useState<Record<string, number>>(() => initialHeldValues(explorer.axes));
  const [trustLevel, setTrustLevel] = useState<"strict" | "balanced" | "wide">("balanced");

  useEffect(() => {
    if (!explorer.outputs.some((output) => output.id === selectedOutput)) {
      setSelectedOutput(bestDefaultOutput(explorer));
    }
  }, [explorer, selectedOutput]);

  useEffect(() => {
    if (layerFilter !== "all" && !explorer.model_layers.some((layer) => layer.id === layerFilter)) {
      setLayerFilter("all");
    }
  }, [explorer.model_layers, layerFilter]);

  useEffect(() => {
    setHeldValues((current) => {
      const next: Record<string, number> = {};
      for (const axis of explorer.axes) {
        const currentValue = current[axis.id];
        next[axis.id] = Number.isFinite(currentValue) ? currentValue! : axisDefaultValue(axis);
      }
      return next;
    });
  }, [explorer.axes]);

  useEffect(() => {
    const axisIds = explorer.axes.map((axis) => axis.id);
    if (!axisIds.length) return;
    const defaultX = explorer.default_axes.x && axisIds.includes(explorer.default_axes.x) ? explorer.default_axes.x : axisIds[0]!;
    const defaultY = explorer.default_axes.y && axisIds.includes(explorer.default_axes.y) && explorer.default_axes.y !== defaultX
      ? explorer.default_axes.y
      : axisIds.find((axis) => axis !== defaultX) ?? defaultX;
    if (!axisIds.includes(focusXAxis)) setFocusXAxis(defaultX);
    if (!axisIds.includes(focusYAxis) || focusYAxis === focusXAxis) setFocusYAxis(defaultY);
  }, [explorer.axes, explorer.default_axes.x, explorer.default_axes.y, focusXAxis, focusYAxis]);

  const visibleRows = useMemo(() => {
    if (layerFilter === "all") return explorer.rows;
    return explorer.rows.filter((row) => row._model_layer === layerFilter);
  }, [explorer.rows, layerFilter]);

  const trustLimits = useMemo(() => trustLimitsForLevel(trustLevel), [trustLevel]);
  const tailorModel = useMemo(
    () => selectedOutput ? fitTailorSliceModel(visibleRows, explorer.axes, selectedOutput) : null,
    [explorer.axes, selectedOutput, visibleRows],
  );
  const tailorEvidence = useMemo(
    () => selectedOutput
      ? summarizeTailorEvidence(visibleRows, tailorModel, selectedOutput, heldValues, focusXAxis, focusYAxis, trustLimits)
      : { raw: 0, compatible: 0, correctedTotal: 0, correctedTrusted: 0, correctedFlagged: 0, correctedRejected: 0, meanHiddenRms: null, maxHiddenJump: null, maxCorrectionDelta: null },
    [focusXAxis, focusYAxis, heldValues, selectedOutput, tailorModel, trustLimits, visibleRows],
  );
  const tailorSuggestions = useMemo(
    () => selectedOutput
      ? recommendTailorRuns({ rows: visibleRows, model: tailorModel, axes: explorer.axes, heldValues, output: selectedOutput, xKey: focusXAxis, yKey: focusYAxis })
      : [],
    [explorer.axes, focusXAxis, focusYAxis, heldValues, selectedOutput, tailorModel, visibleRows],
  );
  const zValueResolver = useMemo(() => {
    if (evidenceMode === "raw" || !selectedOutput) return undefined;
    return (context: { row: Record<string, unknown>; xKey: string; yKey: string; zKey: string }) =>
      tailorValueForMode(evidenceMode, context.row, tailorModel, selectedOutput, heldValues, context.xKey, context.yKey, trustLimits);
  }, [evidenceMode, heldValues, selectedOutput, tailorModel, trustLimits]);
  const visibleHeldAxes = useMemo(() => {
    const correlated = (explorer.correlations[selectedOutput] ?? []).map((item) => item.field);
    return explorer.axes
      .filter((axis) => axis.id !== focusXAxis && axis.id !== focusYAxis)
      .sort((left, right) => {
        const leftIndex = correlated.indexOf(left.id);
        const rightIndex = correlated.indexOf(right.id);
        return (leftIndex < 0 ? 999 : leftIndex) - (rightIndex < 0 ? 999 : rightIndex);
      })
      .slice(0, 5);
  }, [explorer.axes, explorer.correlations, focusXAxis, focusYAxis, selectedOutput]);

  const numericColumns = useMemo<CorrelogramColumn[]>(() => {
    if (!selectedOutput) return [];
    const allColumns = [...explorer.axes, ...explorer.outputs];
    const byId = new Map(allColumns.map((column) => [column.id, column]));
    const correlationFields = (explorer.correlations[selectedOutput] ?? []).map((item) => item.field);
    const ranked = rankColumnsByCorrelationTo(
      visibleRows,
      allColumns.map((column) => ({ field: column.id, headerName: column.label })),
      selectedOutput,
    ).map((score) => score.field);
    const fields = Array.from(new Set([
      selectedOutput,
      ...correlationFields,
      ...explorer.axes.map((axis) => axis.id),
      ...ranked,
    ])).filter((field) => visibleRows.some((row) => numericValue(row[field]) !== null));
    return fields.slice(0, 24).map((field) => {
      const column = byId.get(field);
      const output = explorer.outputs.find((item) => item.id === field);
      const corr = explorer.correlations[selectedOutput]?.find((item) => item.field === field);
      const role: CorrelogramColumn["role"] = output ? "response" : explorer.axes.some((axis) => axis.id === field) ? "condition" : "derived";
      return {
        key: field,
        header: column?.label ?? labelFor(field),
        role,
        evidence: output?.evidence ?? corr?.evidence ?? "observed",
        source: output?.evidence === "model-conditioned" || corr?.evidence === "model-conditioned" ? "model" : "observed",
      };
    });
  }, [explorer, selectedOutput, visibleRows]);

  const groups = useMemo(() => {
    if (explorer.model_layers.length <= 1) return undefined;
    return explorer.model_layers.map((layer, index) => ({
      key: layer.id,
      label: layer.label,
      color: layerColor(index),
      rows: visibleRows.filter((row) => row._model_layer === layer.id),
    })).filter((group) => group.rows.length > 0);
  }, [explorer.model_layers, visibleRows]);

  const selectedRowIdx = selectedRunId ? visibleRows.findIndex((row) => String(row._row_id ?? row.runId ?? row.name) === selectedRunId || rowKey(row as ThreadRow) === selectedRunId) : -1;
  const selectedRow = selectedRowIdx >= 0 ? visibleRows[selectedRowIdx] : null;
  const selectedTailorAudit = selectedRow && selectedOutput
    ? correctionForPair(selectedRow, tailorModel, selectedOutput, heldValues, focusXAxis, focusYAxis, trustLimits)
    : null;
  const output = explorer.outputs.find((item) => item.id === selectedOutput);
  const storedWarning = explorer.warnings[0]?.includes("corrected/model-transported") && tailorModel
    ? "No stored corrected rows were found; Tailor is synthesizing corrected-to-slice evidence from the fitted response model."
    : explorer.warnings[0];
  const evidenceWarning = evidenceMode === "compatible" && tailorEvidence.compatible < 3
    ? `Too few compatible points at this held slice (${tailorEvidence.compatible}). Read correlations as model-conditioned until Tailor samples this region.`
    : evidenceMode === "corrected" && tailorEvidence.correctedTrusted < 6
      ? `Few trusted corrected points at this held slice (${tailorEvidence.correctedTrusted}). Loosen trust or request direct samples.`
      : null;
  const warning = evidenceWarning ?? storedWarning ?? (query.error ? "Production ADB route was unavailable; showing the current V5 table fallback." : null);
  const topCorrelations = explorer.correlations[selectedOutput] ?? [];

  return (
    <main className="v5-main v5-adb-page">
      <section className="v5-adb-shell">
        <header className="v5-adb-toolbar">
          <div className="v5-adb-title">
            <span className="v5-eyebrow">ADB Generator</span>
            <strong>{projectName}</strong>
          </div>
          <label className="v5-adb-select">
            <span>Output</span>
            <select value={selectedOutput} onChange={(event) => setSelectedOutput(event.target.value)}>
              {explorer.outputs.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
            </select>
          </label>
          <div className="v5-adb-layer-tabs" aria-label="Model layer filter">
            <button type="button" className={layerFilter === "all" ? "is-active" : ""} onClick={() => setLayerFilter("all")}>All</button>
            {explorer.model_layers.map((layer) => (
              <button key={layer.id} type="button" className={layerFilter === layer.id ? "is-active" : ""} onClick={() => setLayerFilter(layer.id)}>
                {layer.label}
              </button>
            ))}
          </div>
          <button className="v5-text-button" type="button" onClick={() => onOpenView("data")}><Icons.Table size={13} />Table</button>
        </header>

        <div className="v5-adb-review">
          <div>
            <strong>{selectedOutput ? selectedOutputLabel(explorer, selectedOutput) : "No output"}</strong>
            <span>{selectedOutput ? reviewSentence(explorer, selectedOutput) : "No numeric outputs were discovered in this project."}</span>
          </div>
          <div className="v5-adb-stats">
            <ExplorerStat label="Rows" value={`${visibleRows.length.toLocaleString()} / ${explorer.row_count.toLocaleString()}`} />
            <ExplorerStat label="Outputs" value={explorer.outputs.length} />
            <ExplorerStat label="Raw" value={tailorEvidence.raw.toLocaleString()} tone={tailorEvidence.raw < 8 ? "warn" : "ok"} />
            <ExplorerStat label="Compatible" value={tailorEvidence.compatible.toLocaleString()} tone={tailorEvidence.compatible < 3 ? "warn" : "ok"} />
            <ExplorerStat label="Corrected" value={`${tailorEvidence.correctedTrusted.toLocaleString()}/${tailorEvidence.correctedTotal.toLocaleString()}`} tone={tailorEvidence.correctedTrusted ? "ok" : "warn"} />
          </div>
        </div>

        <div className={`v5-adb-warning ${warning ? "" : "is-empty"}`} aria-hidden={warning ? undefined : true}>
          {warning ? <><Icons.Info size={14} />{warning}</> : null}
        </div>

        <section className="v5-adb-tailor-strip" aria-label="Tailor slice controls">
          <div className="v5-adb-evidence-tabs" aria-label="Point evidence mode">
            {TAILOR_EVIDENCE_MODES.map((mode) => (
              <button
                key={mode.id}
                type="button"
                className={evidenceMode === mode.id ? "is-active" : ""}
                onClick={() => setEvidenceMode(mode.id)}
                title={mode.title}
              >
                {mode.label}
              </button>
            ))}
          </div>
          <label className="v5-adb-compact-select">
            <span>X</span>
            <select value={focusXAxis} onChange={(event) => setFocusXAxis(event.target.value)}>
              {explorer.axes.map((axis) => <option key={axis.id} value={axis.id}>{axis.label}</option>)}
            </select>
          </label>
          <label className="v5-adb-compact-select">
            <span>Y</span>
            <select value={focusYAxis} onChange={(event) => setFocusYAxis(event.target.value)}>
              {explorer.axes.map((axis) => <option key={axis.id} value={axis.id}>{axis.label}</option>)}
            </select>
          </label>
          <label className="v5-adb-compact-select" title="Corrected-point trust gate. Strict hides larger hidden-axis moves; wide keeps more model-conditioned evidence visible.">
            <span>Trust</span>
            <select value={trustLevel} onChange={(event) => setTrustLevel(event.target.value as "strict" | "balanced" | "wide")}>
              <option value="strict">Strict</option>
              <option value="balanced">Balanced</option>
              <option value="wide">Wide</option>
            </select>
          </label>
          <div className="v5-adb-held-inline" title="Held-axis constants for the current ADB slice">
            {visibleHeldAxes.map((axis) => (
              <label key={axis.id}>
                <span>{axis.label}</span>
                <input
                  type="number"
                  value={Number.isFinite(heldValues[axis.id]) ? heldValues[axis.id] : axisDefaultValue(axis)}
                  step="any"
                  onChange={(event) => setHeldValues((current) => ({ ...current, [axis.id]: Number(event.target.value) }))}
                />
              </label>
            ))}
          </div>
          <div className="v5-adb-tailor-audit">
            <span>{evidenceModeDescription(evidenceMode)}</span>
            <strong>{tailorModel ? `fit ${compactNumber(tailorModel.rmse)} RMSE` : "fit unavailable"}</strong>
            {evidenceMode === "corrected" ? <strong>max move {compactNumber(tailorEvidence.maxHiddenJump)}</strong> : null}
          </div>
        </section>

        <section className="v5-adb-matrix" aria-label="ADB correlogram">
          {query.isLoading && !query.data && dataLoading ? <div className="v5-adb-loading"><Icons.Refresh size={18} />Loading database outputs...</div> : null}
          {numericColumns.length >= 2 && visibleRows.length ? (
            <Suspense fallback={<div className="v5-adb-loading"><Icons.Refresh size={18} />Loading correlogram...</div>}>
              <DataExplorerCorrelogram
                rows={visibleRows}
                numericColumns={numericColumns}
                groups={groups && groups.length > 1 ? groups : undefined}
                colorByKey={groups && groups.length > 1 ? undefined : "_model_layer"}
                zAxisKey={selectedOutput || undefined}
                zAxisLabel={selectedOutput ? `${selectedOutputLabel(explorer, selectedOutput)} · ${evidenceMode}` : undefined}
                zValueResolver={zValueResolver}
                selectedRowIdx={selectedRowIdx >= 0 ? selectedRowIdx : undefined}
                maxColumns={14}
                defaultPruneBoringAxes
                defaultHideWeakCorrelations
                weakCorrelationThreshold={0.16}
                onPointClick={(rowIdx) => {
                  const row = visibleRows[rowIdx];
                  if (row) onSelectRun(String(row._row_id ?? row.runId ?? row.name ?? rowKey(row as ThreadRow)), String(row._node_id ?? row.rowNodeId ?? "") || null);
                }}
              />
            </Suspense>
          ) : (
            <div className="v5-adb-empty">
              <Icons.GridIcon size={22} />
              <strong>Need more numeric data</strong>
              <span>ADB exploration needs at least two numeric fields and one output in the selected layer.</span>
            </div>
          )}
        </section>

        <details className="v5-adb-drawer">
          <summary><span>Evidence, held axes, and output catalog</span><Icons.ChevronDown size={14} /></summary>
          <div className="v5-adb-drawer-grid">
            <section>
              <h3>Held axes</h3>
              <div className="v5-adb-held-grid">
                {explorer.axes.slice(0, 16).map((axis) => (
                  <label key={axis.id} title={`${axis.sample_count} samples, range ${compactNumber(axis.min)} to ${compactNumber(axis.max)}`}>
                    <span>{axis.label}</span>
                    <input
                      type="number"
                      step="any"
                      value={Number.isFinite(heldValues[axis.id]) ? heldValues[axis.id] : axisDefaultValue(axis)}
                      onChange={(event) => setHeldValues((current) => ({ ...current, [axis.id]: Number(event.target.value) }))}
                    />
                  </label>
                ))}
              </div>
            </section>
            <section>
              <h3>Top correlations</h3>
              <div className="v5-adb-correlation-list">
                {topCorrelations.slice(0, 8).map((item) => (
                  <span key={item.field} className={item.evidence === "model-conditioned" ? "is-model" : ""}>
                    <em>{item.label}</em>
                    <strong>{formatValue(item.r)}</strong>
                  </span>
                ))}
              </div>
            </section>
            <section>
              <h3>Corrected point audit</h3>
              {selectedTailorAudit ? (
                <div className="v5-adb-audit-list">
                  <span><em>Status</em><strong>{selectedTailorAudit.status}</strong></span>
                  <span><em>Hidden RMS</em><strong>{compactNumber(selectedTailorAudit.hiddenRms)}</strong></span>
                  <span><em>Max jump</em><strong>{compactNumber(selectedTailorAudit.hiddenMaxDelta)}</strong></span>
                  <span><em>Correction delta</em><strong>{compactNumber(selectedTailorAudit.correctionDelta)}</strong></span>
                  <span><em>Trust</em><strong>{compactNumber(selectedTailorAudit.trustScore)}</strong></span>
                </div>
              ) : (
                <p>Select a run to inspect how its value moves onto the current held slice.</p>
              )}
            </section>
            <section>
              <h3>Available outputs</h3>
              <div className="v5-adb-chip-list">
                {explorer.outputs.map((item) => (
                  <button key={item.id} type="button" className={item.id === selectedOutput ? "v5-adb-chip is-active" : "v5-adb-chip"} onClick={() => setSelectedOutput(item.id)}>
                    {item.label} <strong>{item.sample_count.toLocaleString()}</strong>
                  </button>
                ))}
              </div>
            </section>
            <section>
              <h3>Model layers</h3>
              <div className="v5-adb-chip-list">
                {explorer.model_layers.map((layer) => (
                  <span key={layer.id} className={`v5-adb-chip ${layer.evidence === "model-conditioned" ? "is-model" : ""}`}>
                    {layer.label} <strong>{layer.count.toLocaleString()}</strong>
                  </span>
                ))}
              </div>
            </section>
            <section>
              <h3>Tailor next runs</h3>
              <div className="v5-adb-next-list">
                {tailorSuggestions.length ? tailorSuggestions.map((suggestion) => (
                  <button key={suggestion.id} type="button" onClick={() => setHeldValues((current) => ({ ...current, ...suggestion.values }))}>
                    <strong>{axisOptionLabel(explorer.axes.find((axis) => axis.id === focusXAxis))} {compactNumber(suggestion.values[focusXAxis])}</strong>
                    <strong>{axisOptionLabel(explorer.axes.find((axis) => axis.id === focusYAxis))} {compactNumber(suggestion.values[focusYAxis])}</strong>
                    <span>{suggestion.reason}</span>
                  </button>
                )) : (
                  <p>Need a fitted response model and two active axes before Tailor can suggest the next slice samples.</p>
                )}
              </div>
            </section>
            {output ? (
              <section>
                <h3>Selected output range</h3>
                <p>{output.label}: {compactNumber(output.min)} to {compactNumber(output.max)} across {output.sample_count.toLocaleString()} samples. Corrected mode is model-conditioned, not direct observation.</p>
              </section>
            ) : null}
          </div>
        </details>
      </section>
    </main>
  );
}
